import { useEffect, useMemo } from 'react';

import { useFeatureClient } from '@ctct/feature-sdk';

import { CheckState, getCheckState } from './helpers';
import { addGate, updateGate, useGates } from './store';
import type { UseFeatureGateProps, UseFeatureGateReturn } from './types';
import { useStatsigTimeout } from './useStatsigTimeout';
import { isL1 } from '../utils';

export const createPersistedKey = (name: string) => `aff-persist-${name}`;

export const getPersistedValue = (name: string) => {
  if (isL1()) {
    const persistedValue = sessionStorage.getItem(createPersistedKey(name));

    return typeof persistedValue === 'string' ? persistedValue === 'true' : null;
  }

  return null;
};

export function useFeatureGate({
  name,
  defaultValue,
  preConditions,
  preConditionsLoading,
  preConditionsError,
}: UseFeatureGateProps): UseFeatureGateReturn {
  const { isLoading: statsigLoading, checkGate } = useFeatureClient();
  const gateValue = useGates(name);

  const loading = preConditionsLoading || statsigLoading;
  const featureState = getCheckState(preConditions, preConditionsError, loading, useStatsigTimeout(loading));

  useEffect(() => {
    addGate(name, getPersistedValue(name) ?? defaultValue);
  }, [name, defaultValue]);

  useEffect(() => {
    if (featureState === CheckState.READY && getPersistedValue(name) === null && gateValue === undefined) {
      updateGate(name, checkGate(name));
    }
  }, [name, featureState, checkGate, gateValue]);

  return useMemo(() => {
    // Treat the gate as "still loading" until we actually have a concrete value.
    // This avoids states like { isLoading: false, data: undefined }
    const isGateValueReady = gateValue !== undefined;

    if (featureState === CheckState.LOADING || !isGateValueReady) {
      return { data: null, isLoading: true, error: null };
    }

    return { data: gateValue, isLoading: false, error: preConditionsError ?? null };
  }, [featureState, preConditionsError, gateValue]);
}
